import React, { useState, useCallback, useEffect, useRef } from "react";
import FormCard from "../FormComponents/FormCard";
import AlertBanner from "../FormComponents/AlertBanner";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import SignatureSection from "../FormComponents/SignatureSection";
import PaymentSection from "../FormComponents/PaymentSection";
import Icon from "../Icons/Icon";

/**
 * Step 7: Wild Card Contract/Invoice
 *
 * Backend keys:
 *
 * participate  String  "yes"|"no"  required
 *   Error key if not selected: errors.participate
 *   If "yes", wild card item rows and payment/signature fields apply.
 *
 * item1ProgramNum … item16ProgramNum   String  optional
 * item1ItemNumber … item16ItemNumber   String  optional
 * item1DollarValue … item16DollarValue String  optional
 * item1OrderQty … item16OrderQty       String  optional
 * item1Measure … item16Measure         String  optional
 * item1ProjectedSales … item16ProjectedSales  String  optional
 * item1UnitEstimate … item16UnitEstimate      String  optional
 * item1InternalUse … item16InternalUse        String  optional
 *
 * total               String  optional
 * paymentMethod       String  optional
 * invoiceAddressText  String  optional
 * signatureName       String  optional  — errors.signatureName
 * signatureDate       String  optional  — errors.signatureDate
 */
const Step7 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    participate: initialContract?.participate || "",
    total: initialContract?.total || "0",
    paymentMethod: initialContract?.paymentMethod || "",
    invoiceAddressText: initialContract?.invoiceAddressText || "",
    signatureName: initialContract?.signatureName || "",
    signatureDate: initialContract?.signatureDate || "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
    // Wild card items - 16 rows
    ...Array.from({ length: 16 }, (_, i) => ({
      [`item${i + 1}ProgramNum`]: initialContract?.[`item${i + 1}ProgramNum`] || "",
      [`item${i + 1}ItemNumber`]: initialContract?.[`item${i + 1}ItemNumber`] || "",
      [`item${i + 1}DollarValue`]: initialContract?.[`item${i + 1}DollarValue`] || "",
      [`item${i + 1}OrderQty`]: initialContract?.[`item${i + 1}OrderQty`] || "",
      [`item${i + 1}Measure`]: initialContract?.[`item${i + 1}Measure`] || "",
      [`item${i + 1}ProjectedSales`]: initialContract?.[`item${i + 1}ProjectedSales`] || "",
      [`item${i + 1}UnitEstimate`]: initialContract?.[`item${i + 1}UnitEstimate`] || "",
      [`item${i + 1}InternalUse`]: initialContract?.[`item${i + 1}InternalUse`] || "",
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
  });

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Wild Card Contract/Invoice</h2>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        <FormCard>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "var(--neutral-800)",
              marginBottom: 8,
            }}
          >
            Wild Card Discounts encourage retail customers to place orders during
            the Show! The best time to CLOSE A SALE is on SHOW DAYS! 90% of all
            exhibitors use the Wild Card discounts to boost Show purchases because
            it works!
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--neutral-600)",
              marginBottom: 10,
            }}
          >
            Exhibitor agrees to provide the following incentives beginning the
            active program date through Tuesday, 10/06/2026. Arett will send an
            invoice based on total wild card items sold during the promo period in
            early/mid October. Payment terms is Net 10.
          </p>
          <AlertBanner type="info">
            Please, only 1 SPIFF per ITEM! No combination deals. All Wild Cards
            subject to Arett Merchant approval.
          </AlertBanner>
          <div
            className="radio-group"
            style={{ marginTop: 16, marginBottom: errors.participate ? 4 : 0 }}
          >
            <label className="custom-radio">
              <input
                type="radio"
                name="participate"
                value="yes"
                checked={formData.participate === "yes"}
                onChange={() => updateField("participate", "yes")}
              />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input
                type="radio"
                name="participate"
                value="no"
                checked={formData.participate === "no"}
                onChange={() => updateField("participate", "no")}
              />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.participate && (
            <span className="field-error" style={{ display: "block", marginBottom: 4 }}>
              {errors.participate}
            </span>
          )}
        </FormCard>

        {formData.participate === "yes" && (
        <>
        <FormCard title="Wild Card Items" icon={<Icon name="card" size={14} />}>
          <p className="text-small mb-12" style={{ color: "var(--danger)" }}>
            *Required Field
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="contract-table">
              <thead>
                <tr>
                  <th>Arett Program #</th>
                  <th>* Arett Item Number</th>
                  <th>* Dollar Value</th>
                  <th>* Order Qty</th>
                  <th>* Wildcard Measure ea; cs; pl</th>
                  <th>Projected Unit Sales</th>
                  <th>Unit of Estimate Total WildCard $</th>
                  <th>Internal Use Only</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 16 }, (_, i) => i + 1).map((num) => (
                  <tr key={num}>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 100 }}
                        value={formData[`item${num}ProgramNum`]}
                        onChange={(e) => updateField(`item${num}ProgramNum`, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 120 }}
                        value={formData[`item${num}ItemNumber`]}
                        onChange={(e) => updateField(`item${num}ItemNumber`, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 80 }}
                        value={formData[`item${num}DollarValue`]}
                        onChange={(e) => updateField(`item${num}DollarValue`, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min={0}
                        style={{ minWidth: 70 }}
                        value={formData[`item${num}OrderQty`]}
                        onChange={(e) => updateField(`item${num}OrderQty`, Math.max(0, parseInt(e.target.value) || 0))}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 120 }}
                        value={formData[`item${num}Measure`]}
                        onChange={(e) => updateField(`item${num}Measure`, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min={0}
                        style={{ minWidth: 100 }}
                        value={formData[`item${num}ProjectedSales`]}
                        onChange={(e) => updateField(`item${num}ProjectedSales`, Math.max(0, parseInt(e.target.value) || 0))}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 120 }}
                        value={formData[`item${num}UnitEstimate`]}
                        onChange={(e) => updateField(`item${num}UnitEstimate`, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        style={{ minWidth: 100 }}
                        value={formData[`item${num}InternalUse`]}
                        readOnly={!isAdmin}
                        onChange={(e) => updateField(`item${num}InternalUse`, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FormCard>

        <PaymentSection
          paymentMethod={formData.paymentMethod}
          invoiceAddressText={formData.invoiceAddressText}
          onPaymentMethodChange={(val) => updateField("paymentMethod", val)}
          onAddressChange={(val) => updateField("invoiceAddressText", val)}
        />
        <SignatureSection
          legal={false}
          name={formData.signatureName}
          date={formData.signatureDate}
          acknowledge={formData.signatureAcknowledge}
          onNameChange={(name) => updateField("signatureName", name)}
          onDateChange={(date) => updateField("signatureDate", date)}
          onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
        />
        {(errors.signatureName || errors.signatureDate) && (
          <div className="step-error-summary" style={{ marginTop: 8 }}>
            {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
            {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
          </div>
        )}
        </>
        )}
      </div>
    </fieldset>
  );
};

export default Step7;
